import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { useAppSettings } from '../context/SettingsContext';
import { getActiveDudaAccount } from '../settings/dudaAccounts';
import {
  listDudaSites,
  getDudaSiteDetail,
  fetchDudaSiteDetailRows,
  fetchDudaSiteAnalytics,
  fetchDudaSiteTrafficAnalytics,
  parseDudaActivityVisits,
  parseDudaActivitiesBreakdown,
  parseDudaSeries,
  DudaSiteDetailRow,
} from '../services/dudaApi';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  if (value !== index) {
    return null;
  }
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function fmtDate(s?: string): string {
  if (!s?.trim()) {
    return '—';
  }
  try {
    const d = parseISO(s);
    return isValid(d) ? format(d, 'PP') : s;
  } catch {
    return s;
  }
}

function publishChipColor(status?: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  if (!status) {
    return 'default';
  }
  if (status === 'PUBLISHED') {
    return 'success';
  }
  if (status === 'NOT_PUBLISHED_YET') {
    return 'warning';
  }
  if (status === 'UNPUBLISHED') {
    return 'default';
  }
  return 'info';
}

function uniqSorted(values: (string | undefined)[]): string[] {
  const set = new Set<string>();
  for (const v of values) {
    if (v?.trim()) {
      set.add(v.trim());
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

const DudaDashboard: React.FC = () => {
  const { settings } = useAppSettings();
  const navigate = useNavigate();
  const dudaAccount = useMemo(() => getActiveDudaAccount(settings), [settings]);
  const { apiUser, apiPassword } = dudaAccount;

  const [detailRows, setDetailRows] = useState<DudaSiteDetailRow[]>([]);
  const [rawBySite, setRawBySite] = useState<Record<string, unknown>>({});
  const [loadingSites, setLoadingSites] = useState(false);
  const [sitesError, setSitesError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterPublish, setFilterPublish] = useState('');
  const [filterEditor, setFilterEditor] = useState('');
  const [filterStore, setFilterStore] = useState('');
  const [filterCertificate, setFilterCertificate] = useState('');
  const [filterLabel, setFilterLabel] = useState('');

  const [selected, setSelected] = useState<DudaSiteDetailRow | null>(null);
  const [tab, setTab] = useState(0);
  const [siteJson, setSiteJson] = useState<unknown>(null);
  const [activitiesJson, setActivitiesJson] = useState<unknown>(null);
  const [trafficJson, setTrafficJson] = useState<unknown>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const credsOk = apiUser.trim() && apiPassword.trim();

  const loadSites = useCallback(async () => {
    if (!credsOk) {
      return;
    }
    setLoadingSites(true);
    setSitesError(null);
    try {
      const list = await listDudaSites(apiUser, apiPassword);
      if (list.length === 0) {
        setDetailRows([]);
        setRawBySite({});
        setSelected(null);
        return;
      }
      const { rows, rawBySite: raw } = await fetchDudaSiteDetailRows(apiUser, apiPassword, list, 5);
      setDetailRows(rows);
      setRawBySite(raw);
      setSelected((prev) => (prev && rows.some((r) => r.site_name === prev.site_name) ? prev : null));
    } catch (e) {
      const msg = axios.isAxiosError(e)
        ? e.message === 'Network Error'
          ? 'Network error — in dev, use npm start (proxy). In production, add a server proxy; browsers cannot call Duda directly due to CORS.'
          : e.response?.data?.message || e.message
        : 'Failed to list sites';
      setSitesError(String(msg));
      setDetailRows([]);
      setRawBySite({});
      setSelected(null);
    } finally {
      setLoadingSites(false);
    }
  }, [apiUser, apiPassword, credsOk]);

  useEffect(() => {
    void loadSites();
  }, [loadSites]);

  const labelOptions = useMemo(() => {
    const all: string[] = [];
    for (const r of detailRows) {
      all.push(...r.labels);
    }
    return uniqSorted(all);
  }, [detailRows]);

  const filterOptions = useMemo(() => {
    return {
      publish: uniqSorted(detailRows.map((r) => r.publish_status)),
      editor: uniqSorted(detailRows.map((r) => r.editor)),
      store: uniqSorted(detailRows.map((r) => r.store_status)),
      certificate: uniqSorted(detailRows.map((r) => r.certificate_status)),
    };
  }, [detailRows]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return detailRows.filter((row) => {
      if (filterPublish && row.publish_status !== filterPublish) {
        return false;
      }
      if (filterEditor && row.editor !== filterEditor) {
        return false;
      }
      if (filterStore && row.store_status !== filterStore) {
        return false;
      }
      if (filterCertificate && row.certificate_status !== filterCertificate) {
        return false;
      }
      if (filterLabel && !row.labels.includes(filterLabel)) {
        return false;
      }
      if (q) {
        const hay = [
          row.site_name,
          row.site_domain,
          row.site_default_domain,
          row.canonical_url,
          row.account_name,
          row.external_uid,
          row.publish_status,
          row.editor,
          row.store_status,
          row.store_type,
          row.certificate_status,
          row.lang,
          ...row.labels,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [
    detailRows,
    searchQuery,
    filterPublish,
    filterEditor,
    filterStore,
    filterCertificate,
    filterLabel,
  ]);

  const clearFilters = () => {
    setSearchQuery('');
    setFilterPublish('');
    setFilterEditor('');
    setFilterStore('');
    setFilterCertificate('');
    setFilterLabel('');
  };

  const hasActiveFilters =
    searchQuery.trim() ||
    filterPublish ||
    filterEditor ||
    filterStore ||
    filterCertificate ||
    filterLabel;

  useEffect(() => {
    if (!selected || !credsOk) {
      setSiteJson(null);
      setActivitiesJson(null);
      setTrafficJson(null);
      return;
    }
    const cached = rawBySite[selected.site_name];
    if (cached !== undefined) {
      setSiteJson(cached);
    } else {
      setSiteJson(null);
    }

    let cancelled = false;
    const run = async () => {
      setDetailLoading(true);
      setDetailError(null);
      try {
        if (cached === undefined) {
          const detail = await getDudaSiteDetail(apiUser, apiPassword, selected.site_name);
          if (!cancelled) {
            setSiteJson(detail);
          }
        }
        const [act, traf] = await Promise.all([
          fetchDudaSiteAnalytics(apiUser, apiPassword, selected.site_name, {
            result: 'activities',
            dateGranularity: 'MONTHS',
          }).catch(() => null),
          fetchDudaSiteTrafficAnalytics(apiUser, apiPassword, selected.site_name, {
            dateGranularity: 'MONTHS',
          }).catch(() => null),
        ]);
        if (cancelled) {
          return;
        }
        setActivitiesJson(act);
        setTrafficJson(traf);
      } catch (e) {
        if (!cancelled) {
          setDetailError(e instanceof Error ? e.message : 'Load failed');
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [selected, apiUser, apiPassword, credsOk, rawBySite]);

  const activityTotal = parseDudaActivityVisits(activitiesJson);
  const seriesActivities = useMemo(() => parseDudaSeries(activitiesJson, 'visitors'), [activitiesJson]);
  const seriesActivitiesForms = useMemo(() => parseDudaSeries(activitiesJson, 'form_submits'), [activitiesJson]);
  const activityBreakdown = useMemo(() => parseDudaActivitiesBreakdown(activitiesJson), [activitiesJson]);
  const seriesTraffic = useMemo(() => parseDudaSeries(trafficJson, 'visitors'), [trafficJson]);
  const isDev = process.env.NODE_ENV === 'development';

  const filterSelect = (
    label: string,
    value: string,
    options: string[],
    onChange: (e: SelectChangeEvent<string>) => void
  ) => (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <InputLabel id={`duda-filter-${label}`}>{label}</InputLabel>
      <Select
        labelId={`duda-filter-${label}`}
        value={value}
        label={label}
        onChange={onChange}
        input={<OutlinedInput label={label} />}
      >
        <MenuItem value="">
          <em>All</em>
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h5" fontWeight={800}>
          Duda · sites & analytics
        </Typography>
        <Chip size="small" label={dudaAccount.label} color="primary" variant="outlined" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Uses the{' '}
        <Link
          href="https://developer.duda.co/reference/getting-started-with-the-duda-api"
          target="_blank"
          rel="noopener noreferrer"
        >
          Duda Partner API
        </Link>{' '}
        — each row is filled from{' '}
        <Link href="https://developer.duda.co/reference/sites-get-site" target="_blank" rel="noopener noreferrer">
          Get Site
        </Link>{' '}
        (<code>GET /sites/multiscreen/&#123;site_name&#125;</code>). Select a site for analytics.
      </Typography>

      <Alert severity="warning" sx={{ mb: 2, maxWidth: 960 }}>
        <strong>Prototype only:</strong> partner credentials belong on a server for real SaaS. In{' '}
        <strong>development</strong>, calls go through <code>/duda-api</code> (see <code>src/setupProxy.js</code>) to
        avoid CORS. A <strong>production</strong> static build still talks to <code>api.duda.co</code> from the
        browser and will usually fail until you add a backend proxy.
      </Alert>

      {isDev && (
        <Alert severity="info" sx={{ mb: 2, maxWidth: 960 }}>
          Dev proxy active: <code>/duda-api/*</code> → <code>https://api.duda.co/api/*</code>
        </Alert>
      )}

      {!credsOk && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Add Duda API user and password under{' '}
          <Button size="small" onClick={() => navigate('/settings#duda')}>
            API settings
          </Button>
          .
        </Alert>
      )}

      <StackRow>
        <Button variant="outlined" onClick={() => void loadSites()} disabled={!credsOk || loadingSites} sx={{ mr: 1 }}>
          Refresh site list
        </Button>
        <Button variant="contained" color="secondary" onClick={() => navigate('/duda/analytics-cache')} sx={{ mr: 1 }}>
          Analytics
        </Button>
        <Button variant="text" endIcon={<OpenInNewIcon />} href="/settings#duda" component="a">
          Credentials
        </Button>
      </StackRow>

      {sitesError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {sitesError}
        </Alert>
      )}

      {loadingSites && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
          <CircularProgress size={22} />
          <Typography variant="body2">Loading sites and Get Site details…</Typography>
        </Box>
      )}

      <Paper sx={{ mt: 2, maxWidth: '100%', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Sites
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Search across names, domains, accounts, labels, and status fields. Filters apply to the table below.
          </Typography>
          <StackRow sx={{ mb: 1.5 }}>
            <TextField
              size="small"
              placeholder="Search…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220, flex: '1 1 200px' }}
            />
            {filterSelect('Publish', filterPublish, filterOptions.publish, (e) =>
              setFilterPublish(e.target.value)
            )}
            {filterSelect('Editor', filterEditor, filterOptions.editor, (e) => setFilterEditor(e.target.value))}
            {filterSelect('Store', filterStore, filterOptions.store, (e) => setFilterStore(e.target.value))}
            {filterSelect('Certificate', filterCertificate, filterOptions.certificate, (e) =>
              setFilterCertificate(e.target.value)
            )}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="duda-filter-label">Label</InputLabel>
              <Select
                labelId="duda-filter-label"
                value={filterLabel}
                label="Label"
                onChange={(e) => setFilterLabel(e.target.value)}
                input={<OutlinedInput label="Label" />}
              >
                <MenuItem value="">
                  <em>Any</em>
                </MenuItem>
                {labelOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button size="small" variant="text" onClick={clearFilters} disabled={!hasActiveFilters}>
              Clear filters
            </Button>
          </StackRow>
          <Typography variant="caption" color="text.secondary">
            Showing {filteredRows.length} of {detailRows.length} site{detailRows.length === 1 ? '' : 's'}
          </Typography>
        </Box>

        <TableContainer sx={{ maxHeight: 560, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Site</TableCell>
                <TableCell>Domain / URL</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Publish</TableCell>
                <TableCell>Editor</TableCell>
                <TableCell>Store</TableCell>
                <TableCell>SSL</TableCell>
                <TableCell>Last published</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Labels</TableCell>
                <TableCell align="right">Links</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detailRows.length === 0 && !loadingSites && credsOk && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Typography color="text.secondary">
                      No sites returned — check credentials or account scope.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredRows.length === 0 && detailRows.length > 0 && !loadingSites && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Typography color="text.secondary">No sites match the current search and filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredRows.map((row) => (
                <TableRow
                  key={row.site_name}
                  hover
                  selected={selected?.site_name === row.site_name}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelected(row);
                    setTab(0);
                  }}
                >
                  <TableCell sx={{ maxWidth: 140 }}>
                    <Tooltip title={row.site_name}>
                      <Typography fontWeight={700} noWrap>
                        {row.site_name}
                      </Typography>
                    </Tooltip>
                    {row.external_uid && (
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {row.external_uid}
                      </Typography>
                    )}
                    {row.template_id != null && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        template {row.template_id}
                      </Typography>
                    )}
                    {row.detailLoadError && (
                      <Chip size="small" color="warning" label="Detail incomplete" sx={{ mt: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Tooltip title={[row.site_domain, row.canonical_url].filter(Boolean).join(' · ')}>
                      <Typography variant="body2" noWrap>
                        {row.site_domain || row.site_default_domain || '—'}
                      </Typography>
                    </Tooltip>
                    {row.canonical_url && row.canonical_url !== row.site_domain && (
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {row.canonical_url}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 160 }}>
                    <Typography variant="body2" noWrap>
                      {row.account_name || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.publish_status ? (
                      <Chip size="small" label={row.publish_status} color={publishChipColor(row.publish_status)} />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {row.editor || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {row.store_status || '—'}
                    </Typography>
                    {row.store_type && (
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {row.store_type}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {row.certificate_status || '—'}
                    </Typography>
                    {row.force_https != null && (
                      <Typography variant="caption" color="text.secondary">
                        HTTPS {row.force_https ? 'on' : 'off'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(row.last_published_date)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(row.creation_date)}</TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {row.labels.slice(0, 4).map((lb) => (
                        <Chip key={lb} size="small" variant="outlined" label={lb} />
                      ))}
                      {row.labels.length > 4 && (
                        <Chip size="small" variant="outlined" label={`+${row.labels.length - 4}`} />
                      )}
                      {row.labels.length === 0 && '—'}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <StackRow sx={{ justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/duda/site/${encodeURIComponent(row.site_name)}`);
                        }}
                      >
                        Analytics
                      </Button>
                      {row.overview_site_url && (
                        <Tooltip title="Overview">
                          <IconLink href={row.overview_site_url} />
                        </Tooltip>
                      )}
                      {row.editor_site_url && (
                        <Tooltip title="Editor">
                          <IconLink href={row.editor_site_url} />
                        </Tooltip>
                      )}
                      {row.preview_site_url && (
                        <Tooltip title="Preview">
                          <IconLink href={row.preview_site_url} />
                        </Tooltip>
                      )}
                      {selected?.site_name === row.site_name && <Chip size="small" label="Selected" color="primary" />}
                    </StackRow>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {selected && (
        <Card sx={{ mt: 3, maxWidth: 1100 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={800}>
                {selected.site_name}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate(`/duda/site/${encodeURIComponent(selected.site_name)}`)}
                startIcon={<OpenInNewIcon fontSize="small" />}
              >
                Open full analytics
              </Button>
            </Box>
            {detailLoading && <InlineSpinner />}
            {detailError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {detailError}
              </Alert>
            )}

            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Site detail" />
              <Tab label="Analytics (activities)" />
              <Tab label="Traffic" />
              <Tab label="Raw JSON" />
            </Tabs>

            <TabPanel value={tab} index={0}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <code>GET /sites/multiscreen/&#123;site_name&#125;</code>
              </Typography>
              <SiteDetailSummary row={selected} />
              <PreBlock data={siteJson} />
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <code>GET /analytics/site/&#123;site_name&#125;</code> with{' '}
                <code>result=activities</code>, <code>dateGranularity=MONTHS</code>
              </Typography>
              {activityTotal != null && (
                <Typography sx={{ mb: 2 }}>
                  Parsed activity total (best-effort): <strong>{activityTotal}</strong>
                </Typography>
              )}
              {seriesActivities.length > 0 && (
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                  Visitors / visits (time buckets)
                </Typography>
              )}
              {seriesActivities.length > 0 ? (
                <Box sx={{ width: '100%', height: 260, mb: 2 }}>
                  <ResponsiveContainer>
                    <BarChart data={seriesActivities}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RechartsTooltip />
                      <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : null}
              {seriesActivitiesForms.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                    Form submits (time buckets)
                  </Typography>
                  <Box sx={{ width: '100%', height: 220, mb: 2 }}>
                    <ResponsiveContainer>
                      <BarChart data={seriesActivitiesForms}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <RechartsTooltip />
                        <Bar dataKey="value" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              )}
              {activityBreakdown.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                    Engagement totals (when Duda returns a single summary object)
                  </Typography>
                  <Box sx={{ width: '100%', height: 240, mb: 1 }}>
                    <ResponsiveContainer>
                      <BarChart data={activityBreakdown} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 11 }} />
                        <RechartsTooltip />
                        <Bar dataKey="value" fill="#0d9488" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              )}
              {seriesActivities.length === 0 &&
                seriesActivitiesForms.length === 0 &&
                activityBreakdown.length === 0 && (
                  <Typography color="text.secondary">
                    No plottable activities data — open Raw JSON to inspect the live shape (Duda uses uppercase fields
                    like <code>FORM_SUBMITS</code>).
                  </Typography>
                )}
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <code>result=traffic</code> — charts read <code>VISITORS</code>, <code>VISITS</code>,{' '}
                <code>PAGE_VIEWS</code> from each row or nested <code>data</code>.
              </Typography>
              {seriesTraffic.length > 0 ? (
                <Box sx={{ width: '100%', height: 280, mb: 2 }}>
                  <ResponsiveContainer>
                    <BarChart data={seriesTraffic}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RechartsTooltip />
                      <Bar dataKey="value" fill="#ea580c" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  No traffic time series parsed — totals-only JSON still appears below.
                </Typography>
              )}
              <PreBlock data={trafficJson} />
            </TabPanel>

            <TabPanel value={tab} index={3}>
              <Typography variant="subtitle2" gutterBottom>
                Activities response
              </Typography>
              <PreBlock data={activitiesJson} />
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Traffic response
              </Typography>
              <PreBlock data={trafficJson} />
            </TabPanel>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

function StackRow({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, ...sx }}>{children}</Box>
  );
}

function IconLink({ href }: { href: string }) {
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', lineHeight: 0 }}>
      <OpenInNewIcon fontSize="small" color="primary" />
    </Link>
  );
}

function SiteDetailSummary({ row }: { row: DudaSiteDetailRow }) {
  const cells: { k: string; v: string }[] = [
    { k: 'Language', v: row.lang || '—' },
    { k: 'Booking', v: row.booking_status || '—' },
    { k: 'GA / tracking', v: row.google_tracking_id || '—' },
    { k: 'Modified', v: fmtDate(row.modification_date) },
    { k: 'First published', v: fmtDate(row.first_published_date) },
  ];
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        gap: 1,
        mb: 2,
      }}
    >
      {cells.map(({ k, v }) => (
        <Paper key={k} variant="outlined" sx={{ p: 1.25 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {k}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {v}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

function InlineSpinner() {
  return (
    <Box sx={{ mb: 2 }}>
      <CircularProgress size={24} />
    </Box>
  );
}

function PreBlock({ data }: { data: unknown }) {
  if (data == null) {
    return <Typography color="text.secondary">No data</Typography>;
  }
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        borderRadius: 1,
        bgcolor: 'grey.100',
        fontSize: 12,
        overflow: 'auto',
        maxHeight: 360,
      }}
    >
      {JSON.stringify(data, null, 2)}
    </Box>
  );
}

export default DudaDashboard;
